import { onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import maintenanceService from '@/services/maintenanceService';
import { isAdmin } from '@/services/auth';

/**
 * Sets up a monitor to periodically check maintenance status
 * @param {number} intervalMinutes - Minutes between each check
 * @returns {Function} - Function to stop the monitor
 */
export function setupMaintenanceMonitor(intervalMinutes = 2) {
  const router = useRouter();
  let intervalId = null;
  
  // Convert minutes to milliseconds
  const intervalMs = intervalMinutes * 60 * 1000;
  
  const checkMaintenanceStatus = async () => {
    try {
      // Verificamos si el cache ha expirado antes de forzar una consulta
      const lastChecked = localStorage.getItem('maintenance_checked');
      const cacheTtl = localStorage.getItem('maintenance_ttl') || '300000'; // 5 minutos por defecto
      const now = new Date().getTime();
      
      // Solo forzamos consulta al servidor si no hay timestamp o si ya expiró
      const shouldForceCheck = !lastChecked || (now - parseInt(lastChecked, 10) >= parseInt(cacheTtl, 10));
      
      // Usar false para respetar la caché si existe y es válida
      const isInMaintenance = await maintenanceService.isInMaintenance(shouldForceCheck);
      
      if (isInMaintenance) {
        // Check if user has privileges for bypass
        const userIsAdmin = isAdmin();
        const hasValidBypass = maintenanceService.hasValidatedBypass();
        
        // IMPORTANTE: Si no es admin o no tiene bypass válido, SIEMPRE redirigir a mantenimiento
        if (!userIsAdmin && !hasValidBypass) {
          router.push({ name: 'Maintenance' });
          return;
        }
      }
    } catch (error) {
      console.error('Error checking maintenance status:', error);
    }
  };
  
  // Start the interval
  intervalId = setInterval(checkMaintenanceStatus, intervalMs);
  
  // Return function to stop the monitor
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
    }
  };
}

/**
 * Composable hook for using in Vue components
 * Starts the monitor when component is mounted and stops it when unmounted
 * @param {number} intervalMinutes - Minutes between each check
 */
export function useMaintenanceMonitor(intervalMinutes = 2) {
  const stopMonitor = setupMaintenanceMonitor(intervalMinutes);
  
  onUnmounted(() => {
    stopMonitor();
  });
  
  return { stopMonitor };
}
